import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { Box, IconButton, Stack, Theme, Typography, useTheme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import CancelRounded from '@mui/icons-material/CancelRounded'

import { ActionCall, PostTypeEnum, ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import { InstagramPost, NetworkType } from '@3m5/sm_publish-frontend-api'

import InstagramOverviewPost from './InstagramOverviewPost'
import NewComment from '../../../../shared/components/ModuleComponents/Post/modules/Comments/NewComment'
import { useTranslation } from 'react-i18next'
import SubHeadline from '../../../../shared/components/ModuleComponents/Post/modules/SubHeadline'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import getDateFormat from '../../../../defaults/getDateFormat'
import { useDispatch, useSelector } from 'react-redux'
import { PostingRootState } from '../../../../store/postingRoot.reducer'
import { namedRequestData } from '../../../../store/common/common.selectors'
import {
  deleteInstagramPostCommentInitiated,
  getIgPageFeedInitiated
} from '../../../../store/instagram/instagram.actions'
import MediaCarousel from '@professocial/professocialFormComponents/FormComponents/MediaCarousel'
import { GetInstagramPageFeedRequest } from '@3m5/sm_publish-frontend-api'
import { getInstagramFeeds } from '../../../../store/instagram/instagram.selector'
import { GetInstagramFeedResponse } from '@3m5/sm_publish-frontend-api'
import VideoPost from '../../../../shared/components/ModuleComponents/Post/modules/VideoPost'

interface IProps extends PropsWithChildren {
  post: InstagramPost
  networkPageId?: string
  videoThumbnail?: string
  disabled?: boolean
  networkType?: NetworkType
  Wrapper?: React.ComponentType<any>
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: '100%',
    width: '100%',
    backgroundColor: 'rgba(0,0,0,0.7)',
    zIndex: 99,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  image: {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
  floating: {
    width: '100%',
    maxWidth: 'calc(100% - 64px - 64px)',
    maxHeight: 'calc(100vh - 90px)',
    margin: 'auto',
  },
  grid: {
    position: 'relative',
    borderRadius: theme.spacing(0.5),
    maxWidth: '100%',
    maxHeight: 'inherit',
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'stretch',
    alignItems: 'stretch',
    justifyContent: 'center',
  },
  imgContainer: {
    flexGrow: 1,
    display: 'flex',
    flexShrink: 1,
    justifyContent: 'center',
    minHeight: '450px',
    overflow: 'hidden',
    flexDirection: 'column',
    alignItems: 'stretch',
    backgroundColor: 'black',
    aspectRatio: '1 / 1',
    flexBasis: '743px',
    borderBottomLeftRadius: '4px',
    borderTopLeftRadius: '4px',
  },
  closeIcon: {
    color: theme.palette.info.main,
    top: 0,
    right: 0,
    position: 'absolute',
    zIndex: 10,
  },
  commentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 2,
    maxWidth: '500px',
    minWidth: '405px',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    borderBottomRightRadius: '4px',
    borderTopRightRadius: '4px',
  },
  commentsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  noComments: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  addComment: {
    marginTop: theme.spacing(0.5),
    borderTop: '1px solid rgb(239,239,239)',
  },
  scrollBoxContainer: {
    position: 'relative',
    flexGrow: 1,
  },
  scrollBox: {
    overflowY: 'scroll',
    position: 'absolute',
    flexGrow: 1,
    left: 0,
    height: '100%',
    width: '100%',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '&::-moz-scrollbar': { display: 'none' },
    scrollbarWidth: 'none',
  },
}))

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<{ width: number | undefined, height: number | undefined }>({
    width: undefined,
    height: undefined,
  })

  useEffect(() => {
    function handleResize () {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

const InstagramLightBox: React.FC<IProps> = ({ children, networkType, post, networkPageId, videoThumbnail, disabled, Wrapper = 'div' }) => {
  const dispatch = useDispatch()

  const [isOpen, setIsOpen] = useState(false)
  const [postData, setPostData] = useState<InstagramPost>(post)
  console.log('post.content', post.content)
  const postType = post.content[0]?.video ? PostTypeEnum.VIDEO : PostTypeEnum.PHOTO
  const social: NetworkType = networkType || NetworkType.Instagram
  const src = postType === PostTypeEnum.VIDEO ? post?.content[0]?.video?.url : (post.content[0]?.image ? post.content[0]?.image.url : '')
  const size = useWindowSize()
  const imageSize = size.width && Math.floor((size.width * 50) / 100)
  const urls: string[] = post.content.map(item => item.image !== undefined ? item.image.url : '')

  const dispatchGetInstagramFeeds = useCallback((params: { data: GetInstagramPageFeedRequest, id: string }) => dispatch(getIgPageFeedInitiated(params)), [dispatch])

  const actionOnDeleteComment: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, deleteInstagramPostCommentInitiated.type))
  const storedInstagramData: GetInstagramFeedResponse | undefined = useSelector(getInstagramFeeds)

  const classes = useStyles()
  const { t } = useTranslation()
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme()

  const toggleIsOpen = () => {
    setIsOpen(!isOpen)
  }

  const handleNewCommentAdded = (value: any) => {
    if (networkPageId) {
      const params: GetInstagramPageFeedRequest = {
        networkPageId,
      }
      dispatchGetInstagramFeeds({ data: params, id: post.networkPostId })
    }
  }

  useEffect(() => {
    actionOnDeleteComment?.status === 'success' &&
    setPostData({ ...postData, comments: postData.comments?.filter(comment => comment.networkCommentId !== actionOnDeleteComment.id) })
  }, [actionOnDeleteComment])

  useEffect(() => {
    setPostData(post)
  }, [post])

  useEffect(() => {
    if (storedInstagramData) {
      const postWithNewComments = storedInstagramData.posts.find(item => item.networkPostId === post.networkPostId)
      postWithNewComments && setPostData(postWithNewComments)
    }
  }, [storedInstagramData])

  return (
    <Wrapper onClick={disabled ? null : toggleIsOpen}>
      {children}
      {isOpen
        ? (
          <Box className={classes.container}>
            <Box className={classes.wrapper}>
              <Box className={classes.floating}>
                <Box className={classes.grid} onClick={(e) => e.stopPropagation()}>
                  <Box sx={{ maxHeight: imageSize, maxWidth: imageSize, flexBasis: imageSize }} className={classes.imgContainer}>
                    <Box>
                      {postType === PostTypeEnum.PHOTO ? (
                        <>
                          {urls !== undefined && urls.length > 1 && imageSize
                            ? <MediaCarousel items={urls} height={imageSize} maxWidth={imageSize} navButtonsAlwaysVisible />
                            : <ImageComponent src={src} fallback='/src/shared/assets/images/icon-img-placeholder.svg' className={classes.image} />}
                        </>
                      ) : (
                        <>
                          {post?.content[0]?.video && <VideoPost value={post.content[0].video} view={ViewEnum.overview}/>}
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box className={classes.commentsContainer}>
                    <IconButton
                      className={`${classes.closeIcon}`}
                      color='default'
                      component='div'
                      onClick={toggleIsOpen}
                      size='large'
                    >
                      <CancelRounded />
                    </IconButton>
                    <Box className={classes.commentsWrapper}>
                      <Box className={classes.scrollBoxContainer}>
                        {!post.content[0]?.text && post.comments?.length === 0 ? (
                          <Box className={classes.noComments}>
                            <Typography variant='h3'>{t('postCommon:noCommentsYet')}</Typography>
                          </Box>
                        ) : (
                          <Box className={classes.scrollBox}>
                            <InstagramOverviewPost
                              networkPageId={networkPageId}
                              post={postData}
                              view={ViewEnum.overview}
                              networkType={NetworkType.Instagram}
                              size={ScreenEnum.xl}
                            />
                          </Box>
                        )}
                      </Box>
                      <Box className={classes.addComment}>
                        <Stack alignItems='flex-start'>
                          {post.postStatistics && post.postStatistics.likeCount > 0 && (
                            <Box sx={{ px: 2, pt: 1 }}>
                              <Typography variant='body2'>
                                {`${t('postCommon:liked')} ${post.postStatistics.likeCount} ${post.postStatistics.likeCount > 1 ? t('postCommon:persons') : t('postCommon:person')}`}
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ px: 2, pb: '5px' }}>
                            <SubHeadline
                              networkPageId={networkPageId}
                              post={post}
                              value={post.postedAt && formatDate(post.postedAt.getTime(), getDateFormat('dateTime'))}
                            />
                          </Box>
                        </Stack>
                        <NewComment
                          networkPageId={networkPageId}
                          post={post}
                          networkType={social}
                          onChange={handleNewCommentAdded}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )
        : null}
    </Wrapper>
  )
}

export default InstagramLightBox
